import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Plus, MapPin, Calendar, Search, X, CheckCircle } from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import StatsCard from '../components/StatsCard';
import ProgressBar from '../components/ProgressBar';

export default function Dashboard() {
  const { user, company } = useAuth();
  const [projects, setProjects] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    name: '', dossierNumber: '', description: '',
    address: '', clientName: '', startDate: '', endDate: '',
  });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const load = async () => {
    try {
      const { data } = await api.get('/projects');
      setProjects(data);
    } catch {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post('/projects', form);
      setShowModal(false);
      setForm({ name: '', dossierNumber: '', description: '', address: '', clientName: '', startDate: '', endDate: '' });
      load();
      toast.success('Chantier créé');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur');
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return projects.filter(p => {
      if (filterStatus && p.status !== filterStatus) return false;
      if (!q) return true;
      return (
        p.name?.toLowerCase().includes(q) ||
        p.dossierNumber?.toLowerCase().includes(q) ||
        p.address?.toLowerCase().includes(q) ||
        p.clientName?.toLowerCase().includes(q) ||
        (p.partners || []).some(pp => pp.partner?.name?.toLowerCase().includes(q))
      );
    });
  }, [projects, search, filterStatus]);

  if (loading) return <div className="container" style={{ padding: 40, textAlign: 'center' }}>Chargement...</div>;

  const isFiltering = search.trim() || filterStatus;
  const active = filtered.filter(p => p.status !== 'COMPLETED');
  const completed = filtered.filter(p => p.status === 'COMPLETED');

  const ProjectCard = ({ p }) => (
    <Link to={`/project/${p.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
      <div className="card" style={{ cursor: 'pointer', transition: 'box-shadow 0.15s' }}>
        <div style={{ marginBottom: 8 }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 700 }}>
            {p.name}
            {p.dossierNumber && (
              <span style={{ fontWeight: 'normal', fontSize: '0.82rem', color: 'var(--text-light)', marginLeft: 8 }}>
                — {p.dossierNumber}
              </span>
            )}
          </h3>
        </div>
        {p.address && (
          <p style={{ fontSize: '0.85rem', color: 'var(--text-light)', marginBottom: 8 }}>
            <MapPin size={12} style={{ verticalAlign: 'middle' }} /> {p.address}
          </p>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <ProgressBar value={p.progress} />
          <span style={{ fontWeight: 600, fontSize: '0.85rem', whiteSpace: 'nowrap' }}>{p.progress}%</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: 'var(--text-light)' }}>
          {p.startDate && (
            <span><Calendar size={11} /> {new Date(p.startDate).toLocaleDateString('fr-FR')}</span>
          )}
          <span>{p._count.photos} photo{p._count.photos !== 1 ? 's' : ''}</span>
        </div>
      </div>
    </Link>
  );

  return (
    <div className="container" style={{ padding: '24px 16px' }}>
      <StatsCard />
      {/* Onboarding : entreprise fraîchement inscrite, page publique pas encore personnalisée */}
      {user?.role === 'ADMIN' && company && !company.logo && !company.tagline && (
        <div style={{
          background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10,
          padding: '14px 18px', marginBottom: 20, display: 'flex', flexWrap: 'wrap',
          alignItems: 'center', justifyContent: 'space-between', gap: 10,
        }}>
          <div style={{ fontSize: '0.9rem', color: '#1e40af', lineHeight: 1.5 }}>
            <strong>Bienvenue !</strong> Personnalisez votre page publique (logo, couleurs, présentation)
            pour qu'elle soit aux couleurs de votre entreprise.
          </div>
          <Link to="/settings" className="btn btn-primary btn-sm">Personnaliser ma page</Link>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2>Mes chantiers ({active.length + completed.length})</h2>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          <Plus size={16} /> Nouveau chantier
        </button>
      </div>

      {/* Filtres */}
      {projects.length > 0 && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: '1 1 220px', minWidth: 180 }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-light)' }} />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Rechercher par dossier, adresse, intervenant…"
              style={{ width: '100%', paddingLeft: 32, paddingRight: search ? 32 : undefined, boxSizing: 'border-box' }}
            />
            {search && (
              <button onClick={() => setSearch('')} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--text-light)', display: 'flex' }}>
                <X size={14} />
              </button>
            )}
          </div>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ flex: '0 0 auto' }}>
            <option value="">Tous les chantiers</option>
            <option value="IN_PROGRESS">En cours</option>
            <option value="COMPLETED">Terminé</option>
          </select>
          {isFiltering && (
            <button className="btn btn-sm" onClick={() => { setSearch(''); setFilterStatus(''); }}>
              <X size={13} /> Réinitialiser
            </button>
          )}
          {isFiltering && (
            <span style={{ fontSize: '0.82rem', color: 'var(--text-light)', whiteSpace: 'nowrap' }}>
              {filtered.length} résultat{filtered.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      )}

      {projects.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 48, color: 'var(--text-light)' }}>
          <p>Aucun chantier pour le moment.</p>
          <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => setShowModal(true)}>
            Créer votre premier chantier
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 48, color: 'var(--text-light)' }}>
          <p>Aucun chantier ne correspond aux critères de recherche.</p>
        </div>
      ) : (
        <>
          {active.length === 0 && !isFiltering ? (
            <div className="card" style={{ textAlign: 'center', padding: 32, color: 'var(--text-light)', marginBottom: 32 }}>
              <p>Aucun chantier en cours.</p>
              <button className="btn btn-primary" style={{ marginTop: 12 }} onClick={() => setShowModal(true)}>
                Nouveau chantier
              </button>
            </div>
          ) : active.length > 0 && (
            <div className="projects-grid" style={{ marginBottom: 40 }}>
              {active.map(p => <ProjectCard key={p.id} p={p} />)}
            </div>
          )}

          {completed.length > 0 && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                <CheckCircle size={18} style={{ color: 'var(--text-light)' }} />
                <h3 style={{ margin: 0, color: 'var(--text-light)', fontSize: '1rem' }}>
                  Chantiers terminés ({completed.length})
                </h3>
              </div>
              <div className="projects-grid" style={{ opacity: 0.7 }}>
                {completed.map(p => <ProjectCard key={p.id} p={p} />)}
              </div>
            </div>
          )}
        </>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Nouveau chantier</h2>
            <form onSubmit={create}>
              <div className="form-group">
                <label>Nom du chantier *</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>N° de dossier</label>
                <input value={form.dossierNumber} onChange={e => setForm({ ...form, dossierNumber: e.target.value })} placeholder="Ex: 2024-001" />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Adresse</label>
                <input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Intervenant / Client</label>
                <input value={form.clientName} onChange={e => setForm({ ...form, clientName: e.target.value })} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Date de début</label>
                  <input type="date" value={form.startDate} onChange={e => setForm({ ...form, startDate: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Date de fin prévue</label>
                  <input type="date" value={form.endDate} onChange={e => setForm({ ...form, endDate: e.target.value })} />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn" onClick={() => setShowModal(false)}>Annuler</button>
                <button type="submit" className="btn btn-primary">Créer</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
